import React from 'react';
import { Eye, SlidersHorizontal, ArrowLeftRight, X } from 'lucide-react';
import { cn } from '../../../lib/utils';
import { RECIPE_TONE_TEXT } from '../interfaces/IReviewRecipe';
import type { IView } from '../interfaces/IView';

export interface ViewStat {
  label: string;
  value: string | number;
}

export interface ViewBoxAction {
  label: string;
  onClick: () => void | Promise<void>;
  icon?: React.ElementType;
  testId?: string;
  disabled?: boolean;
}

export interface ViewBoxContextAction {
  label: string;
  onClick?: () => void | Promise<void>;
  onDismiss: () => void | Promise<void>;
  icon?: React.ElementType;
  testId?: string;
  dismissLabel?: string;
}

interface ViewBoxProps {
  /** The currently-applied view, or null for the neutral/default state. */
  activeView: IView | null;
  /** Scope/impact metrics (e.g. resources / visible / dimmed). */
  stats?: readonly ViewStat[];
  /** Opens the ViewsGallery. */
  onChangeView: () => void;
  /** Opens rule authoring for power users; omit to hide the Edit button. */
  onEdit?: () => void;
  /** Optional mode-specific action, rendered inside the view box. */
  modeAction?: ViewBoxAction;
  /** Optional applied filter/view context with an inline reset affordance. */
  contextAction?: ViewBoxContextAction;
  /** Title shown when no view is active. */
  defaultTitle?: string;
  /** Description shown when no view is active. */
  defaultDescription?: string;
  changeLabel?: string;
  editLabel?: string;
  className?: string;
}

/**
 * ViewBox — the universal hero of every assistant surface. It names the
 * ACTIVE view in plain language, shows its scope/impact, and offers one-tap
 * "Change view" (→ ViewsGallery) and "Edit" (→ rule authoring). Generalized
 * from the Compass/Blueprint "AWS Review" card so every diagram type opens
 * to the same comfy "here's what you're looking at; tap to look differently"
 * moment — the product's hook.
 */
export const ViewBox: React.FC<ViewBoxProps> = ({
  activeView,
  stats,
  onChangeView,
  onEdit,
  modeAction,
  contextAction,
  defaultTitle = 'All',
  defaultDescription = 'The full diagram, nothing dimmed. Pick a view to focus your work.',
  changeLabel = 'Change view',
  editLabel = 'Edit',
  className,
}) => {
  const Icon = activeView?.icon ?? Eye;
  const title = activeView?.title ?? defaultTitle;
  const description = activeView?.description ?? defaultDescription;
  const toneClass = activeView ? RECIPE_TONE_TEXT[activeView.tone] : 'text-muted-foreground';
  const ModeActionIcon = modeAction?.icon;
  const ContextActionIcon = contextAction?.icon;

  return (
    <div
      className={cn(
        'rounded-lg border border-border bg-background p-3',
        className,
      )}
      data-testid="assistant-view-box"
    >
      <div className="flex items-start gap-2.5">
        <span className={cn('mt-0.5 shrink-0', toneClass)}>
          <Icon className="h-5 w-5" />
        </span>
        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-2">
            <span className="block truncate text-[13px] font-semibold text-foreground">{title}</span>
            {activeView?.badge ? <span className="shrink-0">{activeView.badge}</span> : null}
          </div>
          <p className="mt-0.5 text-[11px] leading-snug text-muted-foreground">{description}</p>
        </div>
      </div>

      {stats && stats.length > 0 && (
        <div className="mt-3 flex items-stretch gap-4">
          {stats.map((stat) => (
            <div key={stat.label} className="min-w-0">
              <div className="text-[15px] font-semibold leading-none text-foreground">{stat.value}</div>
              <div className="mt-1 text-[9px] font-medium uppercase tracking-wide text-muted-foreground">
                {stat.label}
              </div>
            </div>
          ))}
        </div>
      )}

      {contextAction && (
        <div
          className="mt-2 flex min-w-0 items-center gap-1 rounded-md border border-border bg-muted/35 px-2 py-1.5 text-[11px] text-muted-foreground"
          data-testid={contextAction.testId}
        >
          <button
            type="button"
            onClick={() => {
              void contextAction.onClick?.();
            }}
            className="flex min-w-0 flex-1 items-center gap-1.5 text-left transition-colors hover:text-foreground"
          >
            {ContextActionIcon ? <ContextActionIcon className="h-3.5 w-3.5 shrink-0" /> : null}
            <span className="truncate">{contextAction.label}</span>
          </button>
          <button
            type="button"
            onClick={() => {
              void contextAction.onDismiss();
            }}
            aria-label={contextAction.dismissLabel ?? 'Clear applied view'}
            className="inline-flex size-5 shrink-0 items-center justify-center rounded text-muted-foreground transition-colors hover:bg-background hover:text-foreground"
          >
            <X className="h-3.5 w-3.5" aria-hidden />
          </button>
        </div>
      )}

      {modeAction && (
        <button
          type="button"
          onClick={() => {
            void modeAction.onClick();
          }}
          data-testid={modeAction.testId}
          disabled={modeAction.disabled}
          className="mt-3 inline-flex w-full items-center justify-center gap-1.5 rounded-md border border-primary/30 bg-primary/5 px-2.5 py-1.5 text-[11px] font-medium text-primary transition-colors hover:bg-primary/10 disabled:cursor-not-allowed disabled:opacity-50"
        >
          {ModeActionIcon ? <ModeActionIcon className="h-3.5 w-3.5" /> : null}
          {modeAction.label}
        </button>
      )}

      <div className="mt-3 flex items-center gap-2">
        <button
          type="button"
          onClick={onChangeView}
          data-testid="view-box-change"
          className="inline-flex flex-1 items-center justify-center gap-1.5 rounded-md border border-border bg-background px-2.5 py-1.5 text-[11px] font-medium text-foreground transition-colors hover:bg-accent hover:text-accent-foreground"
        >
          <ArrowLeftRight className="h-3.5 w-3.5" />
          {changeLabel}
        </button>
        {onEdit && (
          <button
            type="button"
            onClick={onEdit}
            data-testid="view-box-edit"
            className="inline-flex flex-1 items-center justify-center gap-1.5 rounded-md border border-border bg-background px-2.5 py-1.5 text-[11px] font-medium text-foreground transition-colors hover:bg-accent hover:text-accent-foreground"
          >
            <SlidersHorizontal className="h-3.5 w-3.5" />
            {editLabel}
          </button>
        )}
      </div>
    </div>
  );
};
